/**
 * Two-player normal-form games: best responses, pure-strategy Nash equilibria, dominant
 * strategies, iterated elimination of strictly dominated strategies, and the fully mixed
 * equilibrium of a 2×2 game.
 *
 * `row[i][j]` and `col[i][j]` are the payoffs to the row and column player when the row
 * player picks strategy i and the column player strategy j. A strategy is a best response
 * if no other strategy does strictly better against the opponent's choice, so ties count
 * (the usual textbook definition) and a game can have several equilibria in one cell
 * block.
 */

export interface Game {
  row: number[][];
  col: number[][];
}

export const size = (game: Game): { rows: number; cols: number } => ({
  rows: game.row.length,
  cols: game.row[0]?.length ?? 0,
});

/** For each cell, whether the row player's payoff is the best in its column. */
export function rowBestResponses(game: Game): boolean[][] {
  const { rows, cols } = size(game);
  const best = Array.from({ length: cols }, (_, j) =>
    Math.max(...game.row.map((r) => r[j]))
  );
  return Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => game.row[i][j] === best[j])
  );
}

/** For each cell, whether the column player's payoff is the best in its row. */
export function colBestResponses(game: Game): boolean[][] {
  const { rows, cols } = size(game);
  const best = Array.from({ length: rows }, (_, i) => Math.max(...game.col[i]));
  return Array.from({ length: rows }, (_, i) =>
    Array.from({ length: cols }, (_, j) => game.col[i][j] === best[i])
  );
}

/** Cells where both players are best-responding: the pure-strategy Nash equilibria. */
export function pureEquilibria(game: Game): [number, number][] {
  const r = rowBestResponses(game);
  const c = colBestResponses(game);
  const found: [number, number][] = [];
  for (const [i, line] of r.entries()) {
    for (const [j, isBest] of line.entries()) {
      if (isBest && c[i][j]) {
        found.push([i, j]);
      }
    }
  }
  return found;
}

export type Dominance = "strict" | "weak" | null;

/** Payoff of the strategy at `index` against each opponent strategy, for either player. */
function payoffs(game: Game, player: "row" | "col", index: number): number[] {
  return player === "row"
    ? game.row[index]
    : game.col.map((line) => line[index]);
}

function count(game: Game, player: "row" | "col"): number {
  const { rows, cols } = size(game);
  return player === "row" ? rows : cols;
}

/** True when `a` does strictly better than `b` against every opponent strategy. */
function strictlyBetter(a: number[], b: number[]): boolean {
  return a.every((value, k) => value > b[k]);
}

/** True when `a` is never worse than `b` and sometimes better. */
function weaklyBetter(a: number[], b: number[]): boolean {
  return (
    a.every((value, k) => value >= b[k]) && a.some((value, k) => value > b[k])
  );
}

/**
 * A player's dominant strategy: one that beats every other strategy against everything
 * the opponent might do (strictly) or never does worse and sometimes better (weakly).
 */
export function dominantStrategy(
  game: Game,
  player: "row" | "col"
): { index: number; kind: "strict" | "weak" } | null {
  const n = count(game, player);
  for (let i = 0; i < n; i++) {
    const mine = payoffs(game, player, i);
    const others = Array.from({ length: n }, (_, k) => k).filter(
      (k) => k !== i
    );
    if (others.every((k) => strictlyBetter(mine, payoffs(game, player, k)))) {
      return { index: i, kind: "strict" };
    }
    if (others.every((k) => weaklyBetter(mine, payoffs(game, player, k)))) {
      return { index: i, kind: "weak" };
    }
  }
  return null;
}

/** Strategies of a player strictly dominated by another pure strategy. */
export function strictlyDominated(game: Game, player: "row" | "col"): number[] {
  const n = count(game, player);
  const result: number[] = [];
  for (let i = 0; i < n; i++) {
    const mine = payoffs(game, player, i);
    for (let k = 0; k < n; k++) {
      if (k !== i && strictlyBetter(payoffs(game, player, k), mine)) {
        result.push(i);
        break;
      }
    }
  }
  return result;
}

export interface Reduction {
  rows: number[];
  cols: number[];
  /** Order in which strategies were removed, as ("row" | "col", original index). */
  removed: ["row" | "col", number][];
}

/** Iterated elimination of strictly dominated pure strategies. */
export function iteratedElimination(game: Game): Reduction {
  const { rows, cols } = size(game);
  let keepRows = Array.from({ length: rows }, (_, i) => i);
  let keepCols = Array.from({ length: cols }, (_, j) => j);
  const removed: ["row" | "col", number][] = [];
  for (;;) {
    const sub: Game = {
      row: keepRows.map((i) => keepCols.map((j) => game.row[i][j])),
      col: keepRows.map((i) => keepCols.map((j) => game.col[i][j])),
    };
    const deadRow = keepRows.length > 1 ? strictlyDominated(sub, "row") : [];
    if (deadRow.length > 0) {
      removed.push(["row", keepRows[deadRow[0]]]);
      keepRows = keepRows.filter((_, k) => k !== deadRow[0]);
      continue;
    }
    const deadCol = keepCols.length > 1 ? strictlyDominated(sub, "col") : [];
    if (deadCol.length > 0) {
      removed.push(["col", keepCols[deadCol[0]]]);
      keepCols = keepCols.filter((_, k) => k !== deadCol[0]);
      continue;
    }
    return { rows: keepRows, cols: keepCols, removed };
  }
}

export interface Mixed {
  /** Probability the row player picks strategy 0. */
  rowProbability: number;
  /** Probability the column player picks strategy 0. */
  colProbability: number;
  rowPayoff: number;
  colPayoff: number;
}

/**
 * The equilibrium of a 2×2 game in which both players mix, or null if there is none
 * (a denominator is zero, or a probability falls outside 0–1 exclusive).
 *
 * Each player mixes so that the OTHER is indifferent between their two strategies:
 * p = (b₁₁ − b₁₀) ÷ (b₀₀ − b₁₀ − b₀₁ + b₁₁) for the row player's probability of strategy 0,
 * and the same with the row player's payoffs a for the column player's q.
 */
export function mixedEquilibrium2x2(game: Game): Mixed | null {
  const { rows, cols } = size(game);
  if (rows !== 2 || cols !== 2) {
    return null;
  }
  const a = game.row;
  const b = game.col;
  const pDenominator = b[0][0] - b[1][0] - b[0][1] + b[1][1];
  const qDenominator = a[0][0] - a[0][1] - a[1][0] + a[1][1];
  if (pDenominator === 0 || qDenominator === 0) {
    return null;
  }
  const p = (b[1][1] - b[1][0]) / pDenominator;
  const q = (a[1][1] - a[0][1]) / qDenominator;
  if (!(p > 0 && p < 1 && q > 0 && q < 1)) {
    return null;
  }
  return {
    rowProbability: p,
    colProbability: q,
    rowPayoff: q * a[0][0] + (1 - q) * a[0][1],
    colPayoff: p * b[0][0] + (1 - p) * b[1][0],
  };
}

/** Cells where both players do strictly better than at cell (i, j). */
export function paretoImprovements(
  game: Game,
  i: number,
  j: number
): [number, number][] {
  const found: [number, number][] = [];
  for (const [r, line] of game.row.entries()) {
    for (const [c, value] of line.entries()) {
      if (value > game.row[i][j] && game.col[r][c] > game.col[i][j]) {
        found.push([r, c]);
      }
    }
  }
  return found;
}
